import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { EmojiClickData } from "emoji-picker-react";

import {
  deletePrivateDiscussionMessage,
  fetchPrivateDiscussionThreads,
  markPrivateDiscussionRead,
  sendPrivateDiscussionMessage,
  uploadCommunityAudio,
  uploadCommunityImage,
  type PrivateDiscussionThread
} from "@/features/public/api";
import {
  hasMemberAccessToken,
  readStoredMemberUser,
  subscribeToMemberSessionChanges,
  withMemberSession as withStoredMemberSession
} from "@/features/public/memberSession";
import { formatAudioDuration } from "@/shared/media/audio";
import VoiceMessagePlayer from "@/shared/media/VoiceMessagePlayer";
import { connectCommunityRealtime } from "@/shared/realtime/communityRealtime";
import { usePressAndHoldRecorder } from "@/shared/media/usePressAndHoldRecorder";

const DISCUSSION_ACCENTS: Record<string, string> = {
  "bg-orange-500": "#f97316",
  "bg-teal-600": "#0d9488",
  "bg-purple-500": "#a855f7",
  "bg-green-600": "#16a34a",
  "bg-blue-500": "#3b82f6",
  "bg-cyan-500": "#06b6d4",
  "bg-amber-500": "#f59e0b",
  "bg-pink-500": "#ec4899",
  "bg-yellow-500": "#eab308",
  "bg-gray-700": "#374151"
};

const LazyEmojiPickerPanel = lazy(() => import("./EmojiPickerPanel"));

function normalizeThreadMembers(thread: PrivateDiscussionThread) {
  return Array.isArray(thread.members)
    ? thread.members.map((member) => ({
        id: member?.id ?? null,
        name: sanitizeDisplayName(member?.name),
        avatar: member?.avatar ?? null,
        online: Boolean(member?.online)
      }))
    : [];
}

function normalizeThreadMessages(thread: PrivateDiscussionThread, currentMemberId: string | null) {
  return Array.isArray(thread.messages)
    ? thread.messages.map((message) => ({
        id: message.id,
        senderId: message.senderId,
        senderName: sanitizeDisplayName(message.senderName),
        senderAvatar: message.senderAvatar ?? null,
        content: message.content ?? "",
        timestamp: message.timestamp,
        isMe: currentMemberId ? message.senderId === currentMemberId : Boolean(message.isMe),
        type: message.type,
        imageUrl: message.imageUrl ?? null,
        audioUrl: message.audioUrl ?? null,
        audioDurationSeconds: message.audioDurationSeconds ?? null,
        readByOthersCount: Number.isFinite(message.readByOthersCount) ? message.readByOthersCount : 0,
        isReadByAllMembers: Boolean(message.isReadByAllMembers)
      }))
    : [];
}

function normalizePrivateDiscussionThread(thread: PrivateDiscussionThread, currentMemberId: string | null): PrivateDiscussionThread {
  return {
    ...thread,
    name: sanitizeDisplayName(thread.name),
    color: typeof thread.color === "string" && thread.color.trim() ? thread.color : "#374151",
    icon: typeof thread.icon === "string" && thread.icon.trim() ? thread.icon : "ri-chat-3-line",
    sport: typeof thread.sport === "string" ? thread.sport : "",
    lastMessage: typeof thread.lastMessage === "string" ? thread.lastMessage : "",
    lastMessageTime: typeof thread.lastMessageTime === "string" ? thread.lastMessageTime : "",
    unread: Number.isFinite(thread.unread) ? thread.unread : 0,
    members: normalizeThreadMembers(thread),
    messages: normalizeThreadMessages(thread, currentMemberId)
  };
}

function sanitizeDisplayName(name: string | null | undefined) {
  const trimmedName = name?.trim();
  return trimmedName && trimmedName.length > 0 ? trimmedName : "Membre";
}

function resolveFirstName(name: string | null | undefined) {
  return sanitizeDisplayName(name).split(/\s+/)[0] ?? "Membre";
}

function resolveDiscussionAccent(color: string) {
  if (color.startsWith("#")) {
    return color;
  }

  return DISCUSSION_ACCENTS[color] ?? "#374151";
}

function formatMessageTime(value: string) {
  return new Date(value).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit"
  });
}

function formatListTime(value: string) {
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function MemberAvatar({ name, avatar }: { name: string; avatar: string | null }) {
  const safeName = sanitizeDisplayName(name);

  if (avatar) {
    return <img src={avatar} alt={safeName} className="w-9 h-9 rounded-full object-cover object-[center_24%]" />;
  }

  return (
    <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-semibold">
      {safeName.charAt(0).toUpperCase()}
    </div>
  );
}

function ChatBubble({
  content,
  audioDurationSeconds,
  audioUrl,
  imageUrl,
  id,
  isMe,
  isSelected = false,
  isDeleting = false,
  onDelete,
  onSelect,
  readByOthersCount,
  senderName,
  senderAvatar,
  timestamp,
  type
}: PrivateDiscussionThread["messages"][number] & {
  isSelected?: boolean;
  isDeleting?: boolean;
  onSelect?: (messageId: string) => void;
  onDelete?: (messageId: string) => void;
}) {
  const safeSenderName = sanitizeDisplayName(senderName);

  if (isMe) {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[75%]">
          <div className="mb-1 flex justify-end">
            {isSelected ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete?.(id);
                }}
                disabled={isDeleting}
                className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-red-500 shadow-sm transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={`${isDeleting ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}`}></i>
                <span>Supprimer</span>
              </button>
            ) : null}
          </div>
          <div
            onClick={() => onSelect?.(id)}
            className={`rounded-2xl transition-all ${isSelected ? "ring-2 ring-[#FFD7C9] ring-offset-2" : ""} ${
              onSelect ? "cursor-pointer" : ""
            }`}
          >
            {type === "IMAGE" && imageUrl ? (
              <img src={imageUrl} alt="Illustration du message" className="rounded-2xl rounded-tr-sm w-full max-w-xs object-cover" />
            ) : type === "AUDIO" && audioUrl ? (
              <div className="rounded-2xl rounded-tr-sm">
                <VoiceMessagePlayer
                  audioUrl={audioUrl}
                  durationSeconds={audioDurationSeconds}
                  label="Votre vocal"
                  tone="orange"
                  compact
                />
              </div>
            ) : (
              <div className="bg-[#E8521A] text-white px-4 py-2.5 rounded-2xl rounded-tr-sm text-sm leading-relaxed">
                {content}
              </div>
            )}
          </div>
          <p className="mt-1 flex items-center justify-end gap-1 text-xs text-gray-400">
            <span>{formatMessageTime(timestamp)}</span>
            <i
              className={readByOthersCount > 0 ? "ri-check-double-line text-[#1A8C7A]" : "ri-check-line text-gray-400"}
              aria-label={readByOthersCount > 0 ? "Lu" : "Envoyé"}
            ></i>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2.5 mb-3">
      <MemberAvatar name={safeSenderName} avatar={senderAvatar} />
      <div className="max-w-[75%]">
        <p className="text-xs text-gray-500 mb-1 font-medium">{safeSenderName}</p>
        {type === "IMAGE" && imageUrl ? (
          <img src={imageUrl} alt="Illustration du message" className="rounded-2xl rounded-tl-sm w-full max-w-xs object-cover" />
        ) : type === "AUDIO" && audioUrl ? (
          <div className="rounded-2xl rounded-tl-sm">
            <VoiceMessagePlayer
              audioUrl={audioUrl}
              durationSeconds={audioDurationSeconds}
              label={safeSenderName}
              tone="teal"
              compact
            />
          </div>
        ) : (
          <div className="bg-gray-100 text-gray-800 px-4 py-2.5 rounded-2xl rounded-tl-sm text-sm leading-relaxed">
            {content}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-1">{formatMessageTime(timestamp)}</p>
      </div>
    </div>
  );
}

function AccessRequired({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ height: "600px" }}>
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-orange-50 text-[#E8521A] mb-4">
          <i className="ri-lock-line text-3xl"></i>
        </div>
        <h3 className="font-bold text-gray-900 mb-2">Connexion requise</h3>
        <p className="text-sm text-gray-500 max-w-md">
          Connectez-vous pour accéder aux discussions privées de vos squads et de vos équipes.
        </p>
        <button
          onClick={onLogin}
          className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors"
        >
          <i className="ri-login-box-line"></i>
          Se connecter
        </button>
      </div>
    </div>
  );
}

function ChatThreadListItem({
  thread,
  isActive,
  onSelect
}: {
  thread: PrivateDiscussionThread;
  isActive: boolean;
  onSelect: (chatId: string) => void;
}) {
  const accent = resolveDiscussionAccent(thread.color);
  const badgeClass = thread.scope === "TEAM" ? "bg-teal-50 text-[#1A8C7A]" : "bg-orange-50 text-[#E8521A]";
  const badgeLabel = thread.scope === "TEAM" ? "Équipe privée" : "Squad privé";

  return (
    <button
      onClick={() => onSelect(thread.id)}
      className={`w-full flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 ${
        isActive ? "bg-orange-50/60" : ""
      }`}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: accent }}
      >
        <i className={`${thread.icon} text-white text-lg`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-gray-900 text-sm truncate">{thread.name}</span>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <span className="text-[11px] text-gray-400">
              {thread.lastMessageTime ? formatListTime(thread.lastMessageTime) : ""}
            </span>
            {thread.unread > 0 ? (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-[#E8521A] px-1.5 py-0.5 text-[10px] font-bold text-white">
                {thread.unread > 99 ? "99+" : thread.unread}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${badgeClass}`}>
            {badgeLabel}
          </span>
          <span className="text-[11px] text-gray-400">{thread.sport}</span>
        </div>
        <div className="mt-1.5 text-xs text-gray-500 truncate">
          {thread.lastMessage || "Aucun message pour le moment"}
        </div>
      </div>
    </button>
  );
}

export default function SquadChatPanel({ initialChatId = null }: { initialChatId?: string | null }) {
  const navigate = useNavigate();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const appliedInitialChatIdRef = useRef<string | null>(null);
  const previousThreadIdRef = useRef<string | null>(null);

  const [threads, setThreads] = useState<PrivateDiscussionThread[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [isVoiceSending, setIsVoiceSending] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(hasMemberAccessToken());
  const [currentMemberId, setCurrentMemberId] = useState<string | null>(readStoredMemberUser()?.id ?? null);
  const hasChatSelection = activeChatId !== null;

  const activeThread = useMemo(
    () => threads.find((thread) => thread.id === activeChatId) ?? null,
    [activeChatId, threads]
  );
  const teamThreads = useMemo(
    () => threads.filter((thread) => thread.scope === "TEAM"),
    [threads]
  );
  const squadThreads = useMemo(
    () => threads.filter((thread) => thread.scope === "SQUAD"),
    [threads]
  );
  const activeThreadId = activeThread?.id ?? null;

  const syncSessionState = useCallback(() => {
    setIsLoggedIn(hasMemberAccessToken());
    setCurrentMemberId(readStoredMemberUser()?.id ?? null);
  }, []);

  useEffect(() => {
    window.addEventListener("storage", syncSessionState);
    const unsubscribe = subscribeToMemberSessionChanges(syncSessionState);

    return () => {
      window.removeEventListener("storage", syncSessionState);
      unsubscribe();
    };
  }, [syncSessionState]);

  const redirectToLogin = useCallback(() => {
    navigate("/formulaires?tab=login");
  }, [navigate]);

  const closeThreadSelection = useCallback(() => {
    setActiveChatId(null);
    appliedInitialChatIdRef.current = null;
    navigate("/social?tab=chat", { replace: true });
  }, [navigate]);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = await withStoredMemberSession((token) => fetchPrivateDiscussionThreads(token));
      syncSessionState();

      if (payload === null) {
        setThreads([]);
        setActiveChatId(null);
        return;
      }

      const normalizedThreads = payload.map((thread) => normalizePrivateDiscussionThread(thread, currentMemberId));

      setThreads(normalizedThreads);
      setActiveChatId((current) => {
        if (current && normalizedThreads.some((thread) => thread.id === current)) {
          return current;
        }

        if (initialChatId && normalizedThreads.some((thread) => thread.id === initialChatId)) {
          appliedInitialChatIdRef.current = initialChatId;
          return initialChatId;
        }

        return normalizedThreads[0]?.id ?? null;
      });
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible de charger les discussions privées");
    } finally {
      setLoading(false);
    }
  }, [currentMemberId, initialChatId, syncSessionState]);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    const container = messagesContainerRef.current;

    if (!container || !activeThreadId) {
      previousThreadIdRef.current = activeThreadId;
      return;
    }

    const hasSwitchedThread = previousThreadIdRef.current !== activeThreadId;

    const frame = window.requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: hasSwitchedThread ? "auto" : "smooth"
      });
    });

    previousThreadIdRef.current = activeThreadId;

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [activeThreadId, activeThread?.messages]);

  useEffect(() => {
    if (!initialChatId || appliedInitialChatIdRef.current === initialChatId) {
      return;
    }

    if (threads.some((thread) => thread.id === initialChatId)) {
      appliedInitialChatIdRef.current = initialChatId;
      setActiveChatId(initialChatId);
    }
  }, [initialChatId, threads]);

  const handleSelectThread = useCallback(
    (chatId: string) => {
      setActiveChatId(chatId);
      appliedInitialChatIdRef.current = chatId;
      navigate(`/social?tab=chat&chatId=${encodeURIComponent(chatId)}`, { replace: true });
    },
    [navigate]
  );

  const upsertThread = useCallback((updatedThread: PrivateDiscussionThread, activate = false) => {
    const normalizedThread = normalizePrivateDiscussionThread(updatedThread, currentMemberId);

    setThreads((current) => {
      const remainingThreads = current.filter((thread) => thread.id !== normalizedThread.id);
      return [normalizedThread, ...remainingThreads];
    });

    if (activate) {
      setActiveChatId(normalizedThread.id);
    }
  }, [currentMemberId]);

  const replaceThread = useCallback((updatedThread: PrivateDiscussionThread) => {
    const normalizedThread = normalizePrivateDiscussionThread(updatedThread, currentMemberId);
    setThreads((current) => current.map((thread) => (thread.id === normalizedThread.id ? normalizedThread : thread)));
  }, [currentMemberId]);

  const sendRecordedVoiceMessage = useCallback(
    async (audioFile: File, audioDurationSeconds: number) => {
      if (!activeThread) {
        return;
      }

      setIsVoiceSending(true);
      setError(null);

      try {
        const updatedThread = await withStoredMemberSession(async (token) => {
          const uploaded = await uploadCommunityAudio(token, audioFile);
          return sendPrivateDiscussionMessage(token, activeThread.id, {
            type: "AUDIO",
            audioUrl: uploaded.url,
            audioPublicId: uploaded.publicId,
            audioResourceType: uploaded.resourceType,
            audioDurationSeconds: audioDurationSeconds || uploaded.duration || undefined
          });
        });
        syncSessionState();

        if (updatedThread === null) {
          redirectToLogin();
          return;
        }

        upsertThread(updatedThread, true);
      } catch (caughtError) {
        setError(caughtError instanceof Error ? caughtError.message : "Impossible d'envoyer ce vocal");
      } finally {
        setIsVoiceSending(false);
      }
    },
    [activeThread, redirectToLogin, syncSessionState, upsertThread]
  );

  const voiceRecorder = usePressAndHoldRecorder({
    disabled: sending || isVoiceSending || !activeThread,
    onRecorded: ({ file, durationSeconds }) => sendRecordedVoiceMessage(file, durationSeconds),
    onError: (message) => {
      setError(message);
    }
  });
  const isMicActive = voiceRecorder.isPreparing || voiceRecorder.isRecording;
  const isVoiceBusy = voiceRecorder.isPreparing || voiceRecorder.isRecording || isVoiceSending;

  useEffect(() => {
    setError(null);
    setSelectedMessageId(null);
    setDeletingMessageId(null);
    setIsEmojiPickerOpen(false);
  }, [activeChatId]);

  useEffect(() => {
    if (!isEmojiPickerOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!emojiPickerRef.current?.contains(event.target as Node)) {
        setIsEmojiPickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isEmojiPickerOpen]);

  useEffect(() => {
    if (!isLoggedIn || !activeThread || activeThread.unread === 0) {
      return;
    }

    let isMounted = true;

    const markActiveThreadAsRead = async () => {
      try {
        const updatedThread = await withStoredMemberSession((token) => markPrivateDiscussionRead(token, activeThread.id));
        syncSessionState();

        if (!updatedThread || !isMounted) {
          return;
        }

        replaceThread(updatedThread);
      } catch {
        // The UI remains usable even if the read marker fails temporarily.
      }
    };

    void markActiveThreadAsRead();

    return () => {
      isMounted = false;
    };
  }, [activeThread, isLoggedIn, replaceThread, syncSessionState]);

  const sendTextMessage = async () => {
    const content = input.trim();

    if (!content || !activeThread) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      const updatedThread = await withStoredMemberSession((token) =>
        sendPrivateDiscussionMessage(token, activeThread.id, {
          content,
          type: "TEXT"
        })
      );
      syncSessionState();

      if (updatedThread === null) {
        redirectToLogin();
        return;
      }

      upsertThread(updatedThread, true);
      setInput("");
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible d'envoyer ce message");
    } finally {
      setSending(false);
    }
  };

  const handleImageSend = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";

    if (!file || !activeThread) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      const updatedThread = await withStoredMemberSession(async (token) => {
        const uploaded = await uploadCommunityImage(token, file);
        return sendPrivateDiscussionMessage(token, activeThread.id, {
          type: "IMAGE",
          imageUrl: uploaded.url,
          imagePublicId: uploaded.publicId,
          imageResourceType: uploaded.resourceType
        });
      });
      syncSessionState();

      if (updatedThread === null) {
        redirectToLogin();
        return;
      }

      upsertThread(updatedThread, true);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible d'envoyer cette image");
    } finally {
      setSending(false);
    }
  };

  const handleEmojiSelect = useCallback((emojiData: EmojiClickData) => {
    setInput((current) => `${current}${emojiData.emoji}`);
    setIsEmojiPickerOpen(false);
    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      if (!activeThread || deletingMessageId) {
        return;
      }

      setDeletingMessageId(messageId);
      setError(null);

      try {
        const updatedThread = await withStoredMemberSession((token) =>
          deletePrivateDiscussionMessage(token, activeThread.id, messageId)
        );
        syncSessionState();

        if (updatedThread === null) {
          redirectToLogin();
          return;
        }

        upsertThread(updatedThread, true);
        setSelectedMessageId(null);
      } catch (caughtError) {
        setError(caughtError instanceof Error ? caughtError.message : "Impossible de supprimer ce message");
      } finally {
        setDeletingMessageId(null);
      }
    },
    [activeThread, deletingMessageId, redirectToLogin, syncSessionState, upsertThread]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    let isCancelled = false;
    let socket: Awaited<ReturnType<typeof connectCommunityRealtime>> = null;

    void (async () => {
      socket = await connectCommunityRealtime({ requiresAuth: true });

      if (!socket || isCancelled) {
        socket?.disconnect();
        return;
      }

      socket.on("community:chat-thread-updated", (updatedThread: PrivateDiscussionThread) => {
        upsertThread(updatedThread);
      });
    })();

    return () => {
      isCancelled = true;
      socket?.disconnect();
    };
  }, [isLoggedIn, upsertThread]);

  if (!isLoggedIn && !loading) {
    return <AccessRequired onLogin={redirectToLogin} />;
  }

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      ) : null}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ height: "600px" }}>
        <div className="flex h-full min-h-0">
          <div className={`w-full lg:w-80 border-r border-gray-100 flex flex-col flex-shrink-0 ${hasChatSelection ? "hidden lg:flex" : "flex"}`}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Mes discussions privées</h3>
                <p className="text-xs text-gray-400 mt-1">Squads et équipes liés à votre compte</p>
              </div>
              <button
                onClick={() => void loadThreads()}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
              >
                <i className="ri-refresh-line"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="m-3 h-24 rounded-2xl bg-gray-50 animate-pulse"></div>
                ))
              ) : threads.length > 0 ? (
                <div>
                  {teamThreads.length > 0 ? (
                    <div>
                      <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#1A8C7A] bg-teal-50 border-b border-teal-100">
                        Équipes
                      </div>
                      {teamThreads.map((thread) => (
                        <ChatThreadListItem
                          key={thread.id}
                          thread={thread}
                          isActive={activeThread?.id === thread.id}
                          onSelect={handleSelectThread}
                        />
                      ))}
                    </div>
                  ) : null}

                  {squadThreads.length > 0 ? (
                    <div>
                      <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#E8521A] bg-orange-50 border-b border-orange-100">
                        Squads
                      </div>
                      {squadThreads.map((thread) => (
                        <ChatThreadListItem
                          key={thread.id}
                          thread={thread}
                          isActive={activeThread?.id === thread.id}
                          onSelect={handleSelectThread}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="p-6 text-center text-sm text-gray-400">
                  <i className="ri-chat-3-line text-3xl mb-3 block"></i>
                  Aucune discussion privée disponible pour ce compte.
                </div>
              )}
            </div>
          </div>

          <div className={`flex-1 min-h-0 ${hasChatSelection ? "flex" : "hidden lg:flex"} flex-col`}>
            {activeThread ? (
              <div key={activeThread.id} className="flex h-full min-h-0 flex-col">
                <div className="flex items-center gap-3 p-4 border-b border-gray-100 bg-white">
                  <button
                    onClick={closeThreadSelection}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 lg:hidden"
                  >
                    <i className="ri-arrow-left-line"></i>
                  </button>
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: resolveDiscussionAccent(activeThread.color) }}
                  >
                    <i className={`${activeThread.icon} text-white text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 text-sm truncate">{activeThread.name}</p>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          activeThread.scope === "TEAM" ? "bg-teal-50 text-[#1A8C7A]" : "bg-orange-50 text-[#E8521A]"
                        }`}
                      >
                        {activeThread.scope === "TEAM" ? "Équipe privée" : "Squad privé"}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {activeThread.members.length} membre{activeThread.members.length > 1 ? "s" : ""} • {activeThread.sport}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-100 overflow-x-auto">
                  {activeThread.members.map((member) => (
                    <div key={`${activeThread.id}-${member.id ?? member.name}`} className="flex flex-col items-center gap-1 flex-shrink-0">
                      <div className="relative">
                        <MemberAvatar name={member.name} avatar={member.avatar} />
                        <span
                          className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${
                            member.online ? "bg-green-500" : "bg-gray-300"
                          }`}
                        ></span>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{resolveFirstName(member.name)}</span>
                    </div>
                  ))}
                </div>

                <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
                  {activeThread.messages.length > 0 ? (
                    activeThread.messages.map((message) => (
                      <ChatBubble
                        key={message.id}
                        {...message}
                        isSelected={message.isMe && selectedMessageId === message.id}
                        isDeleting={deletingMessageId === message.id}
                        onSelect={message.isMe ? setSelectedMessageId : undefined}
                        onDelete={message.isMe ? handleDeleteMessage : undefined}
                      />
                    ))
                  ) : (
                    <div className="h-full flex items-center justify-center text-center text-sm text-gray-400 px-6">
                      Aucun message pour l'instant. Lancez la discussion avec votre squad ou votre équipe.
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-gray-100 bg-white">
                  <div className="relative space-y-2">
                    {isEmojiPickerOpen && !isVoiceBusy ? (
                      <div
                        ref={emojiPickerRef}
                        className="absolute bottom-full left-0 z-20 mb-3 overflow-hidden rounded-3xl border border-[#F3D6CC] bg-white shadow-[0_20px_45px_rgba(232,82,26,0.14)]"
                      >
                        <Suspense
                          fallback={
                            <div className="flex h-[400px] w-[320px] items-center justify-center bg-white text-sm text-[#7B5A4D]">
                              <span className="inline-flex items-center gap-2">
                                <i className="ri-loader-4-line animate-spin text-[#E8521A]"></i>
                                Chargement des emojis...
                              </span>
                            </div>
                          }
                        >
                          <LazyEmojiPickerPanel onEmojiClick={handleEmojiSelect} />
                        </Suspense>
                      </div>
                    ) : null}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => fileRef.current?.click()}
                        disabled={sending || isVoiceBusy}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                      >
                        <i className="ri-image-line text-sm"></i>
                      </button>
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageSend} />
                      <button
                        type="button"
                        onClick={() => setIsEmojiPickerOpen((current) => !current)}
                        disabled={sending || isVoiceBusy}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                        aria-label="Ouvrir les emojis"
                      >
                        <i className="ri-emotion-happy-line text-sm"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          setIsEmojiPickerOpen(false);
                          if (voiceRecorder.isPreparing) {
                            voiceRecorder.cancelRecording();
                            return;
                          }

                          if (voiceRecorder.isRecording) {
                            voiceRecorder.stopRecording();
                            return;
                          }

                          void voiceRecorder.startRecording();
                        }}
                        disabled={sending || isVoiceSending}
                        className={`inline-flex h-11 w-11 items-center justify-center rounded-full text-white transition-all flex-shrink-0 ${
                          isMicActive
                            ? "bg-[#1A8C7A] shadow-[0_12px_24px_rgba(26,140,122,0.24)] ring-2 ring-[#CFEDE8]"
                            : "bg-[#E8521A] hover:bg-[#d44a16]"
                        } ${sending || isVoiceSending ? "opacity-60 cursor-not-allowed" : ""}`}
                        aria-label={isMicActive ? "Arrêter et envoyer le vocal" : "Démarrer un vocal"}
                      >
                        <span className="relative flex h-5 w-5 items-center justify-center">
                          <i className={`${isMicActive ? "ri-send-plane-fill" : "ri-mic-fill"} text-lg`}></i>
                          {isMicActive ? (
                            <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border border-white bg-[#1A8C7A] animate-pulse"></span>
                          ) : null}
                        </span>
                      </button>
                      <div className="flex-1">
                        {isMicActive ? (
                          <div className="flex h-11 items-center gap-3 rounded-2xl border border-[#F3D6CC] bg-[#FFF6F1] px-4 text-sm text-[#8C3D1A] shadow-[0_8px_18px_rgba(232,82,26,0.06)]">
                            <span className="flex h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#E8521A] animate-pulse"></span>
                            <span className="font-mono text-sm font-semibold text-[#E8521A]">
                              {formatAudioDuration(voiceRecorder.durationSeconds)}
                            </span>
                            <div className="ml-auto flex items-end gap-1" aria-hidden="true">
                              {[8, 14, 10, 18, 11, 16].map((height, index) => (
                                <span
                                  key={`voice-bar-${index}`}
                                  className="block w-1 rounded-full bg-[#E8521A] animate-pulse"
                                  style={{ height, animationDelay: `${index * 0.1}s` }}
                                ></span>
                              ))}
                            </div>
                          </div>
                        ) : isVoiceSending ? (
                          <div className="flex h-11 items-center justify-center rounded-2xl border border-[#D8ECE8] bg-[#F4FBF9] px-4 text-sm text-[#1A8C7A] shadow-[0_8px_18px_rgba(26,140,122,0.06)]">
                            <i className="ri-loader-4-line mr-2 animate-spin"></i>
                            <span className="font-medium">Envoi du vocal...</span>
                          </div>
                        ) : (
                          <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                void sendTextMessage();
                              }
                            }}
                            placeholder="Écrire un message..."
                            className="w-full px-4 py-2.5 bg-gray-100 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                          />
                        )}
                      </div>
                      <button
                        onClick={() => void sendTextMessage()}
                        disabled={sending || isVoiceBusy || !input.trim()}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#E8521A] text-white hover:bg-[#d44a16] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                      >
                        <i className="ri-send-plane-fill text-sm"></i>
                      </button>
                    </div>
                    {!voiceRecorder.isSupported ? (
                      <p className="text-xs text-amber-600">Votre navigateur ne prend pas en charge l'enregistrement vocal direct.</p>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : hasChatSelection ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-orange-50 text-[#E8521A] mb-4">
                  <i className="ri-error-warning-line text-3xl"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Discussion indisponible</h3>
                <p className="text-sm text-gray-500 max-w-md">
                  Cette conversation n&apos;a pas pu être chargée correctement. Revenez à la liste puis réessayez.
                </p>
                <div className="mt-5 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={closeThreadSelection}
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Retour
                  </button>
                  <button
                    type="button"
                    onClick={() => void loadThreads()}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#E8521A] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#d44a16]"
                  >
                    <i className="ri-refresh-line"></i>
                    Réessayer
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-orange-50 text-[#E8521A] mb-4">
                  <i className="ri-chat-3-line text-3xl"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Sélectionnez une discussion</h3>
                <p className="text-sm text-gray-500">
                  Choisissez un squad ou une équipe dans la liste pour commencer à discuter.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
